/**
 * `P1-UIK-026`, the design-system round: **the mockups' visual language, named
 * once, so six front ends read as ONE product.**
 *
 * The founder's amendment: *"Stand up `@hakili/ui` as the shared design system
 * carrying the mockups' visual language , tokens, components, the stub chip ,
 * so all apps read as ONE polished product, not several drafts."* And the rule
 * that makes it honest: *"The mockups ARE the complete design , faithful
 * realization, not new design work. Bind spacing, type, components, and copy
 * from the mockup, never from invention."*
 *
 * ## Every value below is COUNTED, not chosen
 *
 * Across `docs/mockups/hakili-{client,bank,console,dealer}.jsx`, 29 428 lines:
 *
 * | token | evidence |
 * | --- | --- |
 * | `SURFACE_BORDER` | `border-gray-200` x555, the single most used class in the four files |
 * | `TEXTE_SECONDAIRE` | `text-gray-500` x533 |
 * | `TEXTE_TERTIAIRE` | `text-gray-400` x442 |
 * | `TEXTE_PRINCIPAL` | `text-gray-900` x115 |
 * | `FOND_PAGE` | `bg-gray-50` x187 |
 * | `ACCENT_FOND` / `ACCENT_TEXTE` | `bg-indigo-600` x180, `text-indigo-600` x175 |
 * | `ACCENT_SURVOL` | `bg-indigo-700` x114 |
 * | `ACCENT_DOUX` | `bg-indigo-50` x116 |
 * | `RAYON_PANNEAU` | `rounded-2xl` x393 |
 * | `RAYON_CONTROLE` | `rounded-xl` x268 |
 * | `RAYON_PASTILLE` | `rounded-full` x264 |
 * | `RAYON_MODALE` | `rounded-3xl` x20, and modals are the ONLY place it appears |
 * | `VOILE_MODALE` | `rgba(17, 24, 39, 0.4)` x22, and the ONLY `rgba` in all four files |
 * | `TITRE_ECRAN` | `text-2xl` x58 with `tracking-tight` x58 |
 * | `TITRE_SECTION` | `text-lg` x39 |
 * | `CORPS` / `METADONNEE` | `text-sm` x944, `text-xs` x1398 |
 * | `CHIFFRE` | `tabular-nums` x167 |
 *
 * **`text-xs` outnumbers everything.** That is not a slip: this is a dense,
 * information-first product where a banker reads a file and a dealer reads a
 * fleet, and the type scale says so. A design system that "improved" it to
 * `text-base` would be redesigning the product under cover of tidying it.
 *
 * ## What is NOT here, and why that is the finding
 *
 * **The tones are already right.** `TONE_CLASSES` reads
 * `bg-emerald-50 text-emerald-700`, `bg-amber-50 text-amber-700`,
 * `bg-rose-50 text-rose-700`, `bg-gray-50 text-gray-500` , which is the
 * mockups' semantic vocabulary character for character (`bg-emerald-50` x107,
 * `text-emerald-700` x101, `text-amber-700` x94, `bg-amber-50` x83,
 * `bg-rose-50` x48). The extraction was already done in round 1 and nobody had
 * said so. This round adds nothing there and `tone.ts` is untouched.
 *
 * **One drift existed and this round cures it by ruling**: `Modal` wore
 * `rounded-2xl` where every mockup modal wears `rounded-3xl`. Every other
 * primitive already agreed , `Button` `rounded-xl`, `Card` `rounded-2xl`,
 * `Chip` and `Badge` `rounded-full`.
 *
 * ## The instrument
 *
 * `tokens.spec.tsx` reads the four mockups and asserts each token below is the
 * class the mockups actually use most for its role. **A token invented here
 * turns that spec red**, which is the difference between a design system
 * derived from a design and one asserted over it.
 */

/** The page a screen sits on. */
export const FOND_PAGE = "bg-gray-50";
/** A raised surface: a card, a panel, a modal. */
export const FOND_SURFACE = "bg-white";
/** The hairline every surface is drawn with. */
export const SURFACE_BORDER = "border-gray-200";
/** A softer divider, inside a surface rather than around it. */
export const SURFACE_DIVISION = "border-gray-100";

/** The sentence a person reads. */
export const TEXTE_PRINCIPAL = "text-gray-900";
/** A label beside a value, a caption under a title. */
export const TEXTE_SECONDAIRE = "text-gray-500";
/**
 * Metadata: a timestamp, a reference, a count nobody acts on.
 *
 * Amendé le 18 octobre 2026 (D-1018-6): `gray-400` sets text at 2.5:1 on white (WCAG AA asks
 * 4.5:1), so it is left to decorative marks , rules, separators, a disabled control's glyph ,
 * and any text a person is meant to read wears `TEXTE_SECONDAIRE` (`gray-500`). The console
 * applies that now. The kit's own components that still wear this token (`StatCard` hint, `Stepper`
 * chevron, `AVenir` context, the close icon of `Modal` and `SidePanel`) serve five apps and
 * their snapshots, and move in the round that takes the other apps' screens (booked, not done).
 */
export const TEXTE_TERTIAIRE = "text-gray-400";

/** The one accent this product has. There is no second. */
export const ACCENT_FOND = "bg-indigo-600";
export const ACCENT_SURVOL = "hover:bg-indigo-700";
export const ACCENT_TEXTE = "text-indigo-600";
/** The accent at rest: a selected rail item, an active filter. */
export const ACCENT_DOUX = "bg-indigo-50";

/**
 * **The alert pastille**, `round F`: the solid dot a rail wears when something
 * is waiting , the only place in this design where a colour is used at full
 * strength rather than as a `-50` ground with a `-700` text.
 *
 * `bg-rose-500` and not `bg-red-500`, and the difference is a decision the
 * founder's design pass made on 6 September rather than a preference of this
 * file. The dealer mockup's own comment at `hakili-dealer.jsx:1101-1104` says
 * it: *« Pastille d'alerte : rose-500 au lieu du red-500 d'origine,
 * harmonisation PROPOSÉE par le système de conception du 6 septembre »*. The
 * 20 August copy of the same file still draws `bg-red-500`, so the pair is
 * readable side by side in the archive.
 *
 * **It is named here and worn nowhere yet.** No surface in this product carries
 * a notification fact today , `notifications` has no route a dealership can
 * call , and `R81` forbids drawing a mark that stands for a count that does not
 * exist. Naming the token now is what makes the day a fact arrives a one-line
 * change in one place instead of a colour chosen again from memory.
 */
export const ALERTE_PASTILLE = "bg-rose-500";

export const RAYON_PANNEAU = "rounded-2xl";
export const RAYON_CONTROLE = "rounded-xl";
export const RAYON_PASTILLE = "rounded-full";
/** Modals only. The mockups use it nowhere else. */
export const RAYON_MODALE = "rounded-3xl";

/**
 * The veil a dialog lays over the page it interrupts.
 *
 * **Counted like every other token, and the count is unusually clean**:
 * `rgba(17, 24, 39, 0.4)` appears 22 times across the four mockups , once in
 * `client`, nine times in `bank`, eight in `console`, four in `dealer` , and it
 * is the ONLY `rgba` value in all 29 428 lines. There is no family to rank it
 * against because the design has exactly one veil. `rgb(17, 24, 39)` is
 * `gray-900`, so the class is the literal, character for character.
 *
 * **`SidePanel` wore `bg-gray-900/20`**, half the drawn opacity , the same
 * shape of drift as `Modal`'s `rounded-2xl` recorded above, and invisible for
 * the same reason: a veil slightly too light still reads as a veil. Both wear
 * this now.
 */
export const VOILE_MODALE = "bg-gray-900/40";

/** A screen's own title, once per screen. */
export const TITRE_ECRAN = "text-2xl font-semibold tracking-tight";
/** A section within a screen. */
export const TITRE_SECTION = "text-lg font-semibold";
/**
 * **The two type roles of the product, by what the text IS, not by where it sits** ,
 * `D-1018-6`, 18 October 2026 (the founder's ratification of the design audit).
 *
 * | role | token | size | what it is |
 * | --- | --- | --- | --- |
 * | reading | `CORPS` | 14 px | any sentence a person reads: a paragraph, a helper line, a reason, a journal entry |
 * | label / metadata | `METADONNEE` | 12 px | a label beside a value, a column header, a chip, a timestamp, a reference, a count |
 *
 * The test that tells them apart is length: **a text of 60 characters or more is a
 * sentence and is `CORPS`**; a label or a figure's caption is short by nature and may
 * stay `METADONNEE`. The console's width contract (`layout-contract.ts` (read inside `console.corridor.spec.ts`)) measures
 * exactly that, so the rule is an instrument and not a taste.
 *
 * Amended 18 October 2026 (D-1018-6): `METADONNEE` was « the most common size in the whole
 * design » and wore every helper paragraph; at 12 px a paragraph computes to 8 px at 67 %
 * zoom. The scale itself is unchanged , `text-xs` and `text-sm` , what changed is which
 * role wears which.
 */
/** Reading: the body of the product, 14 px. */
export const CORPS = "text-sm";
/** Label and metadata, 12 px: short text only. */
export const METADONNEE = "text-xs";

/**
 * **The width of a page's content column**, `D-1018-6`, 18 October 2026: `max-w-7xl`,
 * 80 rem, 1280 px at the root font size. It was `max-w-6xl` (1152 px) , « amendé le 18 octobre
 * 2026 (D-1018-6) » , because at 80 % and 67 % zoom the old bound left ~500 px of dead margin
 * on each side while the text shrank with the zoom. One column for every screen: a screen that
 * sets its own `max-w-*` inside it is a screen whose left edge is not its neighbour's, which is
 * what the audit's `DX-002` measured (1104 px, 1024 px and 768 px columns in one console).
 */
export const PAGE_LARGEUR = "max-w-7xl";
/**
 * **The only underline of the product**, `D-1019-2`, 19 October 2026: an inline link in running prose , a
 * word of a sentence that is a link. A list row, a card, a chip, a button, a filter, a tab, the navigation, a
 * figure: none is underlined, and an anchor is plain by the stylesheet (`styles.css`). Hover is a colour.
 */
export const LIEN_TEXTE = "text-indigo-700 underline underline-offset-2 hover:text-indigo-900";

/**
 * A text-only button or link that is NOT in a sentence (« Mot de passe oublié ? », « Ouvrir », « Voir »):
 * indigo, no underline, a soft ground on hover. Focus is the global ring (`styles.css`).
 */
export const BOUTON_TEXTE = "rounded-lg text-indigo-700 hover:bg-indigo-50";

/** The measure of a form or a block of prose INSIDE the column: left-aligned, never a second column. */
export const PAGE_MESURE = "max-w-3xl";
/**
 * Any figure a person compares against another figure.
 *
 * `tabular-nums` x167 in the mockups, and it is load-bearing rather than
 * decorative: a column of prices in proportional digits does not line up, and a
 * banker comparing 452 000 against 398 000 is doing it with his eye.
 */
export const CHIFFRE = "tabular-nums";

/*
 * `F-0a` (BANK-V2, `docs/design/bank-v2/D3-build-map.md` section 4.2): **the bank's class constants.**
 *
 * Everything above is the six-app palette and is untouched. These name the bank's own look, which lives in
 * `bank-theme.css` as `--hk-*` variables scoped under `[data-app="bank"]` (R-4: IBM Plex and the bank palette, for
 * the bank only). A `BANK_*` class resolves only inside a bank document: in any other app the variable is undefined
 * and the class does nothing, which is the point of a scoped theme and not a defect.
 *
 * They are Tailwind arbitrary-value classes over a variable, with the type hint Tailwind needs to tell a colour from a
 * length (`bg-[color:…]`, `text-[length:…]`). None writes a colour, a shade or a font name: `tokens.spec.ts` refuses
 * that, refuses a constant whose variable the theme does not define, and `no-app-colour` has nothing to refuse
 * because there is no literal colour to find.
 */

/** Surfaces. */
export const BANK_BG_PAGE = "bg-[color:var(--hk-bg)]";
export const BANK_BG_SURFACE = "bg-[color:var(--hk-surface)]";
/** A quiet band inside a card. */
export const BANK_BG_SURFACE_2 = "bg-[color:var(--hk-surface-2)]";
export const BANK_BG_NEUTRAL = "bg-[color:var(--hk-neutral-bg)]";
/** The hairline around a surface, and the divider inside one. */
export const BANK_BORDER = "border-[color:var(--hk-line)]";
export const BANK_BORDER_STRONG = "border-[color:var(--hk-line-strong)]";
export const BANK_BORDER_SOFT = "border-[color:var(--hk-line-soft)]";

/** Text: the sentence a person reads (17.1:1 on a surface), and labels and metadata (7.5:1). */
export const BANK_TEXT = "text-[color:var(--hk-text)]";
export const BANK_TEXT_2 = "text-[color:var(--hk-text-2)]";

/** The rail. */
export const BANK_BG_NAV = "bg-[color:var(--hk-nav)]";
export const BANK_TEXT_NAV = "text-[color:var(--hk-nav-text)]";

/** The one accent. White on `--hk-brand` reads 6.3:1. */
export const BANK_BG_BRAND = "bg-[color:var(--hk-brand)]";
export const BANK_BG_BAD_SOLID = "bg-[color:var(--hk-bad)]";
export const BANK_BG_BRAND_HOVER = "hover:bg-[color:var(--hk-brand-strong)]";
export const BANK_BG_BRAND_TINT = "bg-[color:var(--hk-brand-tint)]";
export const BANK_BG_BRAND_WASH = "bg-[color:var(--hk-brand-wash)]";
export const BANK_TEXT_BRAND = "text-[color:var(--hk-brand-strong)]";
export const BANK_RING_FOCUS = "ring-[color:var(--hk-focus)]";

/** The signals, each a text/background pair at 4.5:1 or better, with its line. */
export const BANK_OK = "bg-[color:var(--hk-ok-bg)] text-[color:var(--hk-ok)]";
/** `KIT-TONES` (D2 `components.css:108`, `.callout.ok{color:#06402B}`): the `ok` CALLOUT's ink, one step darker than the `ok` signal's own. */
export const BANK_OK_CALLOUT = "bg-[color:var(--hk-ok-bg)] text-[color:var(--hk-ok-ink)]";
/** `KIT-TONES` (D2 `components.css:109`, `:110`): the `warn` and `bad` CALLOUTS' inks (#52300A, #6E120D), darker than the signals' own. */
export const BANK_WARN_CALLOUT = "bg-[color:var(--hk-warn-bg)] text-[color:var(--hk-warn-ink)]";
export const BANK_BAD_CALLOUT = "bg-[color:var(--hk-bad-bg)] text-[color:var(--hk-bad-ink)]";
export const BANK_OK_BORDER = "border-[color:var(--hk-ok-line)]";
export const BANK_WARN = "bg-[color:var(--hk-warn-bg)] text-[color:var(--hk-warn)]";
export const BANK_WARN_BORDER = "border-[color:var(--hk-warn-line)]";
/**
 * `A4` part 2 (architect's ruling, 31 October 2026): the dealer's warn NOTICE wears the bank's warn values (`--hk-warn-bg`,
 * `--hk-warn-line` as `bank-theme.css` resolves them, which the designer sampled as #FFEFCF and #E6BE70), not Tailwind's amber.
 * The dealer never loads the bank's theme, so the variables are undefined there and the fallbacks ARE the bank's values;
 * `tokens.spec.ts` reads them out of `bank-theme.css` and refuses a drift. The bank's own values are never changed.
 */
export const DEALER_WARN_NOTICE =
  "border-[color:var(--hk-warn-line,#e6be70)] bg-[color:var(--hk-warn-bg,#ffefcf)]";
/**
 * `P1-CNW-063` (the architect's note of 2 November 2026, Designer R's review): **the kit's error tone, for the apps that do not load the bank's theme.** The bank's `--hk-bad` (#A82019) is defined under
 * `[data-app="bank"]` only, so in the console and the dealer's app `var(--hk-bad)` alone is undefined and the class draws nothing. As `DEALER_WARN_NOTICE` does, the variable carries its own value as the fallback:
 * the fallback IS the bank's value, `tokens.spec.ts` reads it out of `bank-theme.css` and refuses a drift, and the bank's own constants and values are not changed. `ERROR_TEXT` is the refusal's sentence and icon;
 * `ERROR_FIELD_BORDER` is the invalid field's border (the strong red, not the bank's pale `--hk-bad-line`).
 */
export const ERROR_TEXT = "text-[color:var(--hk-bad,#a82019)]";
export const ERROR_FIELD_BORDER = "border-[color:var(--hk-bad,#a82019)]";
/** D2 `.verdict.sig`: the register's « signalé » class, the brand tint with its own ink (`--hk-sig-ink`, D2's #34307A). */
export const BANK_SIGNAL = "bg-[color:var(--hk-brand-tint)] text-[color:var(--hk-sig-ink)]";
export const BANK_BAD = "bg-[color:var(--hk-bad-bg)] text-[color:var(--hk-bad)]";
export const BANK_BAD_BORDER = "border-[color:var(--hk-bad-line)]";

/** The underwriter's bay: a dark surface, its two texts, its line and its two signals. */
export const BANK_BG_BAY = "bg-[color:var(--hk-bay-0)]";
export const BANK_BG_BAY_DEEP = "bg-[color:var(--hk-bay-1)]";
export const BANK_TEXT_BAY = "text-[color:var(--hk-bay-text)]";
export const BANK_TEXT_BAY_2 = "text-[color:var(--hk-bay-text-2)]";
export const BANK_BORDER_BAY = "border-[color:var(--hk-bay-line)]";
export const BANK_TEXT_BAY_OK = "text-[color:var(--hk-bay-ok)]";
export const BANK_TEXT_BAY_WARN = "text-[color:var(--hk-bay-warn)]";

/** The faces. Plex Mono is for figures a banker compares. */
export const BANK_FONT = "font-[family-name:var(--hk-font)]";
export const BANK_FONT_MONO = "font-[family-name:var(--hk-mono)]";

/** The type scale: 12 px only for an upper-case micro-label of 24 characters or fewer; 14 px is the floor for a sentence. */
export const BANK_FS_MICRO = "text-[length:var(--hk-fs-micro)]";
export const BANK_FS_META = "text-[length:var(--hk-fs-meta)]";
export const BANK_FS_BODY = "text-[length:var(--hk-fs-body)]";
export const BANK_FS_READ = "text-[length:var(--hk-fs-read)]";
export const BANK_FS_LEAD = "text-[length:var(--hk-fs-lead)]";
export const BANK_FS_H4 = "text-[length:var(--hk-fs-h4)]";
export const BANK_FS_H3 = "text-[length:var(--hk-fs-h3)]";
export const BANK_FS_H2 = "text-[length:var(--hk-fs-h2)]";
export const BANK_FS_H1 = "text-[length:var(--hk-fs-h1)]";
export const BANK_FS_FIGURE = "text-[length:var(--hk-fs-figure)]";

/** The radii. */
export const BANK_RADIUS_SM = "rounded-[var(--hk-r-sm)]";
export const BANK_RADIUS_MD = "rounded-[var(--hk-r-md)]";
export const BANK_RADIUS_LG = "rounded-[var(--hk-r-lg)]";
export const BANK_RADIUS_PILL = "rounded-[var(--hk-r-pill)]";

/**
 * `F-0b`: **a figure in the bank**, Plex Mono with tabular digits at 0,94 em of its surroundings (the prototype's
 * `.num`). The face is the theme's, so outside a bank document the class sets only the digits and the size.
 */
export const BANK_FIGURE =
  "font-[family-name:var(--hk-mono)] [font-variant-numeric:var(--hk-num)] text-[length:var(--hk-fs-num)]";

/** `F-0b`: the focus ring of every bank control, a 3 px outline in the theme's focus colour. */
export const BANK_FOCUS = "focus-visible:[outline:3px_solid_var(--hk-focus)]";

/** `F-0c`: the control radius (10 px): inputs, selects, segmented controls, steppers, buttons. */
export const BANK_RADIUS_CTL = "rounded-[var(--hk-r-ctl)]";

/** `F-0c`: the ink fill (the text colour as a surface): the pressed state of an `ink` segmented control. */
export const BANK_BG_INK = "bg-[color:var(--hk-text)]";

/**
 * `F-0c`, R-3: **the bank's page widths.** A list screen keeps 1 280 px; a work screen (a dossier) is a column of at most
 * 1 180 px PLUS the bay (`--hk-bay`, 408 px, 368 under 1 440 px).
 */
export const BANK_PAGE_LIST = "max-w-[var(--hk-w-list)]";
export const BANK_PAGE_WORK = "max-w-[var(--hk-w-work)]";

/** `F-1`: the body's line height (1,5), landing with the page's ground, text colour and size. */
export const BANK_LH_BODY = "leading-[var(--hk-lh-body)]";

/** `F-1`: the dark rail's states (D2): hover, the current entry (with its 3 px bar), and the initials tile. */
export const BANK_NAV_HOVER = "hover:bg-[color:var(--hk-nav-hover)]";
export const BANK_NAV_ACTIVE = "bg-[color:var(--hk-nav-active)] shadow-[inset_3px_0_0_var(--hk-nav-bar)]";
export const BANK_BG_NAV_AVATAR = "bg-[color:var(--hk-nav-avatar)]";

/**
 * `F-1b`: the bank's work grid and its bay slot as class strings, so `PageColumn` and a spec that puts a bay on a real route
 * read the SAME classes. The bay sits beside the column from 1 100 px up and stacks under it below 1 100 (D2: « 64 px under
 * 1 100 px … bay 368 px from 1 100 to 1 439 px »), i.e. the breakpoint is `max-[1099px]`, not `max-[1100px]`.
 */
export const WORK_GRID_CLASSES =
  "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_var(--hk-bay)] max-[1099px]:grid-cols-1";
export const BAY_SLOT_CLASSES =
  "sticky top-0 h-screen min-w-0 overflow-hidden max-[1099px]:static max-[1099px]:h-auto";
